import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";

import { Drawer, Divider, Button } from "antd";

import { AppContext } from "../../../../../App";
import TransactionListItem from "../../components/IncomeExpenses/Transactions/TransactionListItem";
import { TransactionCard } from "../IncomeExpenses/ProfileIncomeExpenses";

type TransactionDrawerProps = {
    open: boolean;
    onClose: () => void;
    transactions: TransactionCard[];
    title?: string;
    // eslint-disable-next-line no-unused-vars
    onUseTotal?: (total: number) => void;
};

const SuggestionsTransactionDrawer: React.FC<TransactionDrawerProps> = ({
    open,
    onClose,
    transactions,
    onUseTotal,
}) => {
    const { isMobile } = useContext(AppContext);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    useEffect(() => {
        if (transactions.length > 0) {
            setSelectedIds(transactions.map(tx => tx.id));
        }
    }, [transactions]);

    const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    const handleToggle = (id: string) => {
        setSelectedIds(prev =>
            (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id])
        );
    };

    const handleUseTotal = useCallback(() => {
        const total = transactions
            .filter(tx => selectedIds.includes(tx.id))
            .reduce((acc, tx) => acc + (tx.totalAmount || 0), 0);
        onUseTotal?.(parseFloat(total.toFixed(2)));
        onClose();
    }, [transactions, selectedIds, onUseTotal, onClose]);

    const totalAmount = transactions
        .filter(tx => selectedIds.includes(tx.id))
        .reduce((acc, tx) => acc + (tx.totalAmount || 0), 0)
        .toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

    return (
        <Drawer
            placement="bottom"
            onClose={onClose}
            closable={false}
            open={open}
            height="70%"
            className={`transaction-drawer ${isMobile ? "mobile" : "desktop"}`}
            styles={{ body: { padding: 16 } }}
            style={{
                borderRadius: "36px 36px 0 0",
                overflow: "hidden",
                width: isMobile ? "100%" : 1000,
                margin: isMobile ? "0" : "0 auto",
            }}
        >
            <div className="transaction-drawer__header">
                <div className="transaction-drawer__top-row">
                    <Button type="link" onClick={onClose} className="transaction-drawer__close-button">
                        Close
                    </Button>
                </div>
                <div className="transaction-drawer__center-text">
                    <div className="transaction-drawer__count">
                        {`${transactions.length} relevant transaction${transactions.length === 1 ? "" : "s"} found.`}
                    </div>
                    <div className="transaction-drawer__total">
                        £{totalAmount}
                        <span className="transaction-drawer__total-label">total</span>
                    </div>
                </div>
            </div>

            <div className="transaction-drawer__list">
                {transactions.length === 0 ? (
                    <p>No transactions to display.</p>
                ) : (
                    <>
                        <div style={{ height: 16 }}/>
                        {transactions.map((tx, idx) => (
                            <div key={tx.id} className="transaction-drawer__item">
                                <TransactionListItem
                                    item={tx}
                                    showMultiselect
                                    selectedTransactions={selectedIdsSet}
                                    toggleTransactionSelection={handleToggle}
                                />
                                {idx < transactions.length - 1 && <Divider/>}
                            </div>
                        ))}
                    </>
                )}
            </div>

            <div className="transaction-drawer__footer">
                <div className="transaction-drawer__footer-buttons">
                    <Button onClick={onClose} style={{ height: 44, flex: 1, fontWeight: 600 }}>
                        Close
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleUseTotal}
                        disabled={selectedIds.length === 0}
                        style={{ height: 44, flex: 1, fontWeight: 600 }}
                    >
                        Use Total
                    </Button>
                </div>
            </div>
        </Drawer>
    );
};

export default SuggestionsTransactionDrawer;
